import React, { useRef, useLayoutEffect } from 'react';
import styled from 'styled-components';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  Container,
  ContainerPadding,
  Text,
  TextAlignment,
  TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import ScrollHintArrow from '../ScrollHintArrow';

gsap.registerPlugin(ScrollTrigger);

const Section = styled.section`
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
`;

const Bar = styled.div`
  position: absolute;
  width: 100%;
  background-color: #c90010;
  display: flex;
  align-items: center;
  overflow: hidden;
`;

const TopBar = styled(Bar)`
  top: 41vh;
  height: auto;
  min-height: 7vh;
  max-height: 7vh;
  transform-origin: top;
  padding: 1vh 0;
  box-sizing: border-box;
`;

const BottomBar = styled(Bar)`
  top: 55vh;
  height: auto;
  min-height: 7vh;
  max-height: 7vh;
  justify-content: flex-end;
  transform-origin: bottom;
  padding: 1vh 0;
  box-sizing: border-box;
`;

const Copy = styled.div`
  font-size: clamp(2rem, 7vh, 6rem);
  color: #c90010;
  background: white;
  padding: 0.5rem 1rem;
  white-space: nowrap;
  font-family: 'Futura Condensed Extra Bold', Futura, Arial, sans-serif;
  font-weight: bold;
  text-transform: uppercase;
  line-height: 1;
`;

const TopCopy = styled(Copy)`
  transform: translateX(-100%);
`;

const BottomCopy = styled(Copy)`
  transform: translateX(100%);
`;

const LogoGTI = styled.img`
  height: 100%;
  max-width: 80vw;
  opacity: 0;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  object-fit: contain;
`;

const ImagesContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
`;

const BackgroundImage = styled.div`
  width: 100%;
  height: 100vh;
  background-image: url('https://assets.volkswagen.com/is/image/volkswagenag/white-background?Zml0PWNyb3AlMkMxJndpZD0xOTIwJmhlaT0xMDgwJmZtdD1qcGVnJnFsdD03OSZiZmM9b2ZmJmRjYzM=');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`;

const GarageImage = styled.div`
  width: 100%;
  height: 100vh;
  background-image: url('https://assets.volkswagen.com/is/image/volkswagenag/garage-1?Zml0PWNyb3AlMkMxJndpZD0xNjk2JmhlaT04NDgmZm10PXBuZy1hbHBoYSZiZmM9b2ZmJjhhZDg=');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  @media (max-width: 560px) {
    background-image: url('https://assets.volkswagen.com/is/image/volkswagenag/gti_image_mobile?Zml0PWNyb3AlMkMxJndpZD0xMDc0JmhlaT0xOTA5JmZtdD1qcGVnJnFsdD03OSZiZmM9b2ZmJjEzOGI=');
  }
`;

const Subtitle = styled(Text)`
  font-family: Futura, Arial;
  font-weight: 800;
  font-style: Condensed ExtraBold;
  leading-trim: NONE;
  line-height: 120%;
  letter-spacing: 0%;
  text-align: center;
  color: #000000;
`;

const SubtitleWrapper = styled(Container)`
  position: absolute;
  top: 70%;
  z-index: 2;
  width: 100%;
  pointer-events: none;
  will-change: opacity, filter;
`;

export default function LimitedUnits() {
  const container = useRef(null);
  const topCopyRef = useRef(null);
  const bottomCopyRef = useRef(null);
  const topBarRef = useRef(null);
  const bottomBarRef = useRef(null);
  const logoRef = useRef(null);
  const imagesContainerRef = useRef(null);
  const subtitleRef = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          id: 'limited-units-pin',
          trigger: container.current,
          start: 'top top',
          end: '+=800%',
          scrub: true,
          pin: true,
          invalidateOnRefresh: true,
        },
      });

      // 🟥 Fase 1: entran los textos
      tl.to(topCopyRef.current, {
        x: '110vw',
        duration: 2,
        ease: 'none',
      });
      tl.to(
        bottomCopyRef.current,
        {
          x: '-110vw',
          duration: 2,
          ease: 'none',
        },
        '<',
      );

      // 🟥 Fase 2: Crece barra superior hasta juntar ambas
      tl.to(topBarRef.current, {
        minHeight: '14vh',
        duration: 1,
        ease: 'none',
      });

      tl.to(subtitleRef.current, {
        opacity: 0,
        filter: 'blur(8px)',
        duration: 1,
        ease: 'none',
      });

      tl.set(logoRef.current, { opacity: 1 });
      tl.set(bottomBarRef.current, { opacity: 0 });
      tl.set(topBarRef.current, { minHeight: '21vh' });

      tl.to(topBarRef.current, {
        backgroundColor: 'rgba(201, 0, 16, 0)',
        duration: 1,
        ease: 'none',
      });

      tl.to(imagesContainerRef.current, {
        y: '-95vh',
        duration: 2,
        ease: 'none',
      });
    }, container);

    return () => ctx.revert();
  }, []);

  return (
    <Section ref={container}>
      <ImagesContainer ref={imagesContainerRef}>
        <BackgroundImage />
        <GarageImage />
      </ImagesContainer>

      <TopBar ref={topBarRef}>
        <TopCopy ref={topCopyRef}>325 Caballos</TopCopy>
        <LogoGTI
          ref={logoRef}
          src="https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-gti50/logo-gti.png"
          alt="Logo GTI"
        />
      </TopBar>
      <BottomBar ref={bottomBarRef}>
        <BottomCopy ref={bottomCopyRef}>325 Unidades</BottomCopy>
      </BottomBar>
      <SubtitleWrapper
        ref={subtitleRef}
        padding={{ right: ContainerPadding.dynamic0250, left: ContainerPadding.dynamic0250 }}
        stretchContent
      >
        <Subtitle textAlign={TextAlignment.center} appearance={TokenTextAppearance.headline300}>
          Un GTI único para quienes entienden que la leyenda no se conduce: se conquista.
        </Subtitle>
      </SubtitleWrapper>
      <ScrollHintArrow pinId="limited-units-pin" color="#000000" showUntil={0.7} />
    </Section>
  );
}
